/** @jsx jsx */
import { jsx } from "theme-ui"
import { useRef, useEffect, useState, useCallback } from "react"
import throttle from "lodash/throttle"
import {
  Scene,
  PerspectiveCamera,
  WebGLRenderer,
  TextureLoader,
  Group,
  HemisphereLight,
  DirectionalLight,
  Raycaster,
} from "three"
import { RemoveScroll } from "react-remove-scroll"
import { gsap } from "gsap"

import globeTexture_2x from "../../../../images/globe/uv-texture@2x.jpg"
import globeTexture from "../../../../images/globe/uv-texture.jpg"

import { OrbitControls } from "./OrbitControls.js"
import createGlobe from "./createGlobe"
import createHelpers from "./createHelpers"
import threeProps from "./threeProps"
import context from "./context"

import useIsDrag from "../../../../utils/useIsDrag"

const debug = false

let globePosition = {
  x: 0,
  y: -27,
  z: 10,
}
let savedControlsPosition = {}
let savedGlobePosition = globePosition
let canvasSize = {
  w: null,
  h: null,
}

const globeY = {
  value: 0.2,
}

const Globe = ({ children, isMobile, view }) => {
  const [isInteractive, setInteractive] = useState(view === "interactive")
  const [intersects, setIntersects] = useState([])
  const [isScrollRemoved, removeScroll] = useState(false)
  const canvasRef = useRef(null)
  const raqRef = useRef()
  const controls = useRef()
  const renderer = useRef()
  const scene = useRef()
  const camera = useRef()
  const raycaster = useRef()
  const globeContainerRef = useRef(new Group())
  const globeRef = useRef()
  const isGlobeReady = useRef(false)
  const withIntroAnim = useRef(
    typeof window !== "undefined" &&
      !window.isLandingAnimationComplete &&
      view !== "interactive"
  )
  const isDragged = useIsDrag(canvasRef)
  const globeYanimation = useRef()

  let markers = useRef([])

  const onMarkerMount = useCallback(marker => {
    markers.current.push(marker)
    if (isGlobeReady.current) {
      globeContainerRef.current.add(marker.object)
    }
  }, [])
  const onMarkerUnmount = useCallback(unmountMarker => {
    markers.current = markers.current.filter(marker => marker !== unmountMarker)
    globeContainerRef.current.remove(unmountMarker.object)
  }, [])

  const handleMouseMove = useCallback(
    event => {
      if (isDragged.current) return

      const mouseX =
        ((event.clientX ||
          (event.changedTouches && event.changedTouches[0].clientX)) /
          canvasSize.w) *
          2 -
        1
      const mouseY =
        -(
          (event.clientY ||
            (event.changedTouches && event.changedTouches[0].clientY)) /
          canvasSize.h
        ) *
          2 +
        1

      raycaster.current.setFromCamera({ x: mouseX, y: mouseY }, camera.current)

      const newIntersects = raycaster.current.intersectObjects(
        globeContainerRef.current.children,
        true
      )

      // we don't count the globe as a valid intersect
      if (
        newIntersects.length &&
        newIntersects[0].object !== globeRef.current
      ) {
        if (
          !intersects.length ||
          intersects[0].object !== newIntersects[0].object
        ) {
          setIntersects(newIntersects)
        }
      } else if (intersects.length > 0) {
        setIntersects([])
      }
    },
    [intersects, isDragged]
  )

  useEffect(() => {
    const canvas = canvasRef.current
    const globeContainer = globeContainerRef.current
    const globe = globeRef.current

    canvasSize = {
      w: canvas.offsetWidth,
      h: canvas.offsetHeight,
    }

    const landingAnimationComplete = () => {
      document.body.style.overflow = "auto"
      window.isLandingAnimationComplete = true
    }

    globeYanimation.current = gsap.to(globeY, {
      value: -0.2,
      duration: 40,
      ease: "none",
      repeat: -1,
      yoyo: true,
      paused: true,
    })

    const globeLandingAnimation = gsap.to(globePosition, {
      y: threeProps.globe.startPosition.y,
      z: threeProps.globe.startPosition.z,
      duration: threeProps.globe.landingDuration,
      ease: "power1.inOut",
      onComplete: landingAnimationComplete,
    })

    // init scene, camera, renderer and globe container
    scene.current = new Scene()
    camera.current = new PerspectiveCamera(
      threeProps.camera.fov,
      canvasSize.w / canvasSize.h,
      threeProps.camera.near,
      threeProps.camera.far
    )
    camera.current.position.z = threeProps.camera.pos.z

    renderer.current = new WebGLRenderer({
      alpha: true,
      antialias: true,
    })

    renderer.current.setPixelRatio(window.devicePixelRatio)
    renderer.current.setSize(canvasSize.w, canvasSize.h)
    canvas.appendChild(renderer.current.domElement)

    scene.current.add(globeContainer)
    /* --- */

    // create lights
    const hemiLight = new HemisphereLight(0xe6e6f1, 0xffffff, 1)
    const dirLight = new DirectionalLight(0xffffff, 0.0666)
    dirLight.position.set(0, 70, 100)
    scene.current.add(hemiLight, dirLight)
    /* --- */

    // load texture and
    // create main globe mesh
    new TextureLoader().load(
      isMobile ? globeTexture : globeTexture_2x,
      texture => {
        isGlobeReady.current = true

        texture.anisotropy = renderer.current.capabilities.getMaxAnisotropy()
        globeRef.current = createGlobe(texture, threeProps.globe)
        globeContainer.add(globeRef.current)
        markers.current.forEach(marker => {
          globeContainer.add(marker.object)
        })
      }
    )

    /* --- */

    controls.current = new OrbitControls(
      camera.current,
      renderer.current.domElement
    )

    controls.current.enableDamping = true
    controls.current.enablePan = false
    controls.current.dampingFactor = 0.2
    controls.current.rotateSpeed = (0.4 * controls.current.getZoom()) / 50

    controls.current.maxDistance = 60
    controls.current.minDistance = threeProps.globe.radius + 7

    const handleControlsChange = () => {
      controls.current.rotateSpeed = (0.4 * controls.current.getZoom()) / 50
    }

    controls.current.addEventListener("change", handleControlsChange)

    raycaster.current = new Raycaster()

    // debug helpers
    if (debug) {
      createHelpers(scene.current)
    }
    /* --- */

    let lastScroll = 0

    const handleScroll = () => {
      const scrollY =
        document.documentElement.scrollTop || document.body.scrollTop
      const yProgress = scrollY / 500 // scroll progress => 0 to 1
      const start = { ...threeProps.globe.startPosition }
      const finish = { ...threeProps.globe.finalPosition }

      const yRange = finish.y - start.y
      const yValue = yProgress * yRange + start.y

      const zRange = finish.z - start.z
      const zValue = yProgress * zRange + start.z

      const newY = yValue
      const newZ = zValue

      if (withIntroAnim.current) {
        globePosition.y = Math.min(threeProps.globe.finalPosition.y, newY)
        globePosition.z = Math.max(threeProps.globe.finalPosition.z, newZ)
      }

      globeContainer.rotation.y += (scrollY - lastScroll) / 1500

      lastScroll = scrollY
    }

    const handleResize = () => {
      const canvas = canvasRef.current
      const canvasSize = {
        w: canvas.offsetWidth,
        h: canvas.offsetHeight,
      }

      camera.current.aspect = canvasSize.w / canvasSize.h
      camera.current.updateProjectionMatrix()
      renderer.current.setSize(canvasSize.w, canvasSize.h)
    }

    const launchLandingAnimation = () => {
      globeLandingAnimation.play()
    }

    const throttleScroll = throttle(handleScroll, 20)
    const throttleResize = throttle(handleResize, 100)

    window.addEventListener("scroll", throttleScroll)
    window.addEventListener("resize", throttleResize)
    window.addEventListener("landingTitleAnimationEnd", launchLandingAnimation)

    // Init
    if (view === "interactive") {
      landingAnimationComplete()
      withIntroAnim.current = false
    }

    if (!window.isLandingAnimationComplete) {
      document.body.style.overflow = "hidden"
      window.scrollTo(0, 0)
    } else {
      globePosition = {
        x: 0,
        y: threeProps.globe.finalPosition.y,
        z: threeProps.globe.finalPosition.z,
      }
      savedGlobePosition = globePosition

      camera.current.position.z = isInteractive
        ? threeProps.camera.pos.z - 10
        : threeProps.camera.pos.z

      globeContainer.rotation.x = globeY.value
    }

    // destroy scene on unmount
    return () => {
      globeRef.current.material.dispose()
      globeContainerRef.current.remove(globe)
      renderer.current.dispose()
      scene.current.dispose()
      controls.current.dispose()

      globeYanimation.current.kill()

      canvas.removeChild(renderer.current.domElement)
      window.removeEventListener("scroll", throttleScroll)
      window.removeEventListener("resize", throttleResize)
      window.removeEventListener(
        "landingTitleAnimationEnd",
        launchLandingAnimation
      )
      controls.current.removeEventListener("change", handleControlsChange)
      landingAnimationComplete()
    }
  }, [])

  useEffect(() => {
    const animate = () => {
      if (!isInteractive) {
        globeContainerRef.current.position.set(
          globePosition.x,
          globePosition.y,
          globePosition.z
        )
        globeContainerRef.current.rotation.x = globeY.value
        globeContainerRef.current.rotation.y += 0.001
      }

      controls.current.update()

      markers.current.forEach(marker => marker.render())

      renderer.current.render(scene.current, camera.current)

      raqRef.current = requestAnimationFrame(animate)
    }

    raqRef.current = requestAnimationFrame(animate)

    return () => {
      cancelAnimationFrame(raqRef.current)
    }
  }, [isInteractive])

  useEffect(() => {
    if (!window.isLandingAnimationComplete) return

    if (view === "interactive") {
      savedControlsPosition = {
        zoom: controls.current.getZoom(),
        rotationLeft: controls.current.getAzimuthalAngle(),
        rotationUp: controls.current.getPolarAngle(),
      }

      savedGlobePosition = {
        ...globePosition,
      }

      if (isInteractive) return

      const animTarget = {
        zoom: controls.current.getZoom(),
      }

      gsap.to(animTarget, {
        zoom: threeProps.camera.pos.z - 10,
        duration: threeProps.globe.viewTransitionDuration,
        ease: "power1.inOut",
        onUpdate: () => {
          controls.current.update(animTarget.zoom)
        },
        onComplete: () => {
          setInteractive(true)
        },
      })

      gsap.to(globePosition, {
        y: 0,
        z: 0,
        duration: threeProps.globe.viewTransitionDuration,
        ease: "power1.inOut",
      })

      globeYanimation.current.pause()
    } else {
      const animTarget = {
        zoom: controls.current.getZoom(),
        rotationLeft: controls.current.getAzimuthalAngle(),
        rotationUp: controls.current.getPolarAngle(),
      }

      setInteractive(false)
      setIntersects([])

      gsap.to(animTarget, {
        zoom: threeProps.camera.pos.z,
        rotationLeft: savedControlsPosition.rotationLeft,
        rotationUp: savedControlsPosition.rotationUp,
        duration: threeProps.globe.viewTransitionDuration,
        ease: "power1.inOut",
        onUpdate: () => {
          controls.current.update(
            animTarget.zoom,
            animTarget.rotationLeft,
            animTarget.rotationUp
          )
        },
      })
      globePosition = { x: 0, y: 0, z: 0 }
      gsap.to(globePosition, {
        y: savedGlobePosition.y,
        z: savedGlobePosition.z,
        duration: threeProps.globe.viewTransitionDuration,
        ease: "power1.inOut",
      })
      globeYanimation.current.play()
    }
  }, [view])

  useEffect(() => {
    let timeout = null

    if (view === "interactive") {
      removeScroll(true)
    } else {
      if (isScrollRemoved) {
        timeout = setTimeout(() => {
          removeScroll(false)
        }, 1000)
      }
    }

    return () => {
      clearTimeout(timeout)
    }
  }, [view])

  return (
    <context.Provider
      value={{
        container: globeContainerRef.current,
        camera: camera.current,
        renderer: renderer.current,
        intersects,
        markers: markers.current,
        onMarkerMount,
        onMarkerUnmount,
      }}
    >
      <RemoveScroll enabled={isScrollRemoved}>
        <div
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100vh",
            zIndex: isInteractive ? 0 : -1,
          }}
        >
          <div
            ref={canvasRef}
            onMouseMove={handleMouseMove}
            onTouchEnd={handleMouseMove}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
            }}
          />
          {children}
        </div>
      </RemoveScroll>
    </context.Provider>
  )
}

export default Globe
